import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Lock, Zap, Archive, Package, AlertTriangle,
  CheckCircle, XCircle, ChevronRight, Loader2, Copy,
  ShieldCheck, Network, Activity, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/lib/api';

function PolicyTab() {
  const [agentId, setAgentId] = useState('');
  const [resource, setResource] = useState('salesforce:accounts:acme');
  const [action, setAction] = useState('read');
  const [context, setContext] = useState('{}');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const evaluate = async () => {
    if (!agentId) { toast.error('Enter an agent ID'); return; }
    setLoading(true);
    try {
      let ctx = {};
      try { ctx = JSON.parse(context); } catch (e) { console.warn('Context parse failed:', e); }
      const data = await api.evaluateAction(agentId, resource, action, ctx);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Evaluation failed');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">Policy Evaluation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input placeholder="Agent ID (agt_...)" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <Input placeholder="Resource (salesforce:accounts:*)" value={resource} onChange={e => setResource(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <select value={action} onChange={e => setAction(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-300 rounded px-3 py-2 text-sm">
            <option value="read">read</option>
            <option value="write">write</option>
            <option value="delete">delete</option>
            <option value="execute">execute</option>
            <option value="create">create</option>
            <option value="invoke">invoke</option>
          </select>
          <Input placeholder='Context JSON: {"value": 5000}' value={context} onChange={e => setContext(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200 font-mono text-sm" />
        </div>
        <Button onClick={evaluate} disabled={loading} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          Evaluate Action
        </Button>
      </div>

      {result && (
        <div className={`rounded-xl p-4 border ${result.allowed ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
          <div className="flex items-center gap-2 mb-2">
            {result.allowed ? <CheckCircle className="w-5 h-5 text-green-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
            <span className={`font-medium ${result.allowed ? 'text-green-400' : 'text-red-400'}`}>
              {result.allowed ? 'ALLOWED' : 'DENIED'}
            </span>
          </div>
          <p className="text-sm text-slate-400">{result.reason}</p>
          {result.conditions_met && Object.keys(result.conditions_met).length > 0 && (
            <div className="mt-2 text-xs text-slate-500 font-mono">
              {JSON.stringify(result.conditions_met, null, 2)}
            </div>
          )}
        </div>
      )}

      <div className="bg-slate-800/20 rounded-xl p-4 border border-slate-700/30">
        <h4 className="text-slate-400 text-sm mb-2">About Policy-Based Execution</h4>
        <p className="text-slate-500 text-xs">
          Agents must not inherit blanket human credentials. Each agent is bound to a PolicySet
          that defines micro-scoped execution limits: what resources it can access, what operations
          it can perform, and under what conditions (value thresholds, time windows, dual-auth).
        </p>
      </div>
    </div>
  );
}

function TrustTab() {
  const [agentId, setAgentId] = useState('');
  const [trustScore, setTrustScore] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchScore = async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const data = await api.getAgentTrustScore(agentId);
      setTrustScore(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch trust score');
    }
    setLoading(false);
  };

  const levelColor = (level) => {
    if (level === 'trusted') return 'text-green-400';
    if (level === 'provisional') return 'text-blue-400';
    if (level === 'degraded') return 'text-yellow-400';
    if (level === 'untrusted') return 'text-red-400';
    return 'text-slate-400';
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">Inter-Agent Trust Score</h3>
        <div className="flex gap-3">
          <Input placeholder="Agent ID" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <Button onClick={fetchScore} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Network className="w-4 h-4 mr-2" />}
            Check Trust
          </Button>
        </div>
      </div>

      {trustScore && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-white">{trustScore.score}</div>
              <div className="text-xs text-slate-400">Score</div>
            </div>
            <div>
              <div className={`text-lg font-bold ${levelColor(trustScore.level)}`}>{trustScore.level}</div>
              <div className="text-xs text-slate-400">Trust Level</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-400">{trustScore.verified}</div>
              <div className="text-xs text-slate-400">Verified</div>
            </div>
            <div>
              <div className="text-lg font-bold text-red-400">{trustScore.failed}</div>
              <div className="text-xs text-slate-400">Failed</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-800/20 rounded-xl p-4 border border-slate-700/30">
        <h4 className="text-slate-400 text-sm mb-2">Zero-Trust Inter-Agent Communication</h4>
        <p className="text-slate-500 text-xs">
          Every agent cryptographically signs its payloads (HMAC-SHA256) and treats data from
          other internal agents with the same suspicion as raw web traffic. Trust scores track
          verification history per agent pair.
        </p>
      </div>
    </div>
  );
}

function CircuitBreakerTab() {
  const [agentId, setAgentId] = useState('');
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState('');

  const fetchBudget = async () => {
    if (!agentId) return;
    setLoading('fetch');
    try {
      const data = await api.getAgentBudget(agentId);
      setBudget(data);
    } catch (err) {
      toast.error('No budget found — create one first');
    }
    setLoading('');
  };

  const createBudget = async () => {
    if (!agentId) return;
    setLoading('create');
    try {
      const data = await api.createAgentBudget(agentId);
      setBudget(data);
      toast.success('Budget created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setLoading('');
  };

  const resetBreaker = async () => {
    if (!agentId) return;
    try {
      await api.resetAgentBreaker(agentId);
      toast.success('Circuit breaker reset');
      fetchBudget();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No tripped breaker');
    }
  };

  const breakerColor = (state) => {
    if (state === 'open') return 'text-red-400 bg-red-500/10';
    if (state === 'half_open') return 'text-yellow-400 bg-yellow-500/10';
    return 'text-green-400 bg-green-500/10';
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">Token Budget & Circuit Breaker</h3>
        <div className="flex gap-3">
          <Input placeholder="Agent ID" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <Button onClick={fetchBudget} disabled={loading === 'fetch'} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Eye className="w-4 h-4 mr-2" />View
          </Button>
          <Button onClick={createBudget} disabled={loading === 'create'} variant="outline" className="border-slate-700 text-slate-300">
            <Zap className="w-4 h-4 mr-2" />Create Budget
          </Button>
        </div>
      </div>

      {budget && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${breakerColor(budget.breaker_state || 'closed')}`}>
              Breaker: {budget.breaker_state || 'closed'}
            </span>
            {(budget.breaker_state === 'open') && (
              <Button onClick={resetBreaker} size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs">
                Reset Breaker
              </Button>
            )}
          </div>
          {budget.limits && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(budget.limits).map(([k, v]) => (
                <div key={k} className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">{k.replace(/_/g, ' ')}</div>
                  <div className="text-lg font-bold text-white">{typeof v === 'number' ? v.toLocaleString() : v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-slate-800/20 rounded-xl p-4 border border-slate-700/30">
        <h4 className="text-slate-400 text-sm mb-2">Token Circuit Breakers</h4>
        <p className="text-slate-500 text-xs">
          Hard execution ceilings prevent runaway agents from burning API budgets.
          Tracks tokens per task/hour/day, sequential loops, child agent spawns, and API call rates.
          Circuit breaker trips automatically when limits are exceeded.
        </p>
      </div>
    </div>
  );
}

function LifecycleTab() {
  const [agentId, setAgentId] = useState('');
  const [lifecycle, setLifecycle] = useState(null);
  const [loading, setLoading] = useState('');

  const fetchLifecycle = async () => {
    if (!agentId) return;
    setLoading('fetch');
    try {
      const data = await api.getAgentLifecycle(agentId);
      setLifecycle(data);
    } catch (err) {
      toast.error('Failed to fetch lifecycle');
    }
    setLoading('');
  };

  const startOffboarding = async () => {
    if (!agentId) return;
    setLoading('offboard');
    try {
      const data = await api.startOffboarding(agentId);
      toast.success(`Offboarding checklist created: ${data.total_tasks} tasks`);
      fetchLifecycle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setLoading('');
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">Agent Lifecycle & Offboarding</h3>
        <div className="flex gap-3">
          <Input placeholder="Agent ID" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <Button onClick={fetchLifecycle} disabled={loading === 'fetch'} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Activity className="w-4 h-4 mr-2" />View Lifecycle
          </Button>
          <Button onClick={startOffboarding} disabled={loading === 'offboard'}
            className="bg-orange-600 hover:bg-orange-700 text-white">
            <Archive className="w-4 h-4 mr-2" />Start Offboarding
          </Button>
        </div>
      </div>

      {lifecycle?.offboarding_checklist && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h4 className="text-white font-medium mb-3">Offboarding Checklist</h4>
          <div className="text-xs text-slate-400 mb-3">
            {lifecycle.offboarding_checklist.critical_tasks} critical, {lifecycle.offboarding_checklist.total_tasks} total tasks
          </div>
          <div className="space-y-2">
            {lifecycle.offboarding_checklist.tasks?.map((t, i) => (
              <div key={i} className="flex items-center gap-3 bg-slate-900/50 rounded-lg p-3">
                <span className={`px-2 py-0.5 rounded text-xs ${t.priority === 'critical' ? 'bg-red-500/10 text-red-400' : t.priority === 'high' ? 'bg-orange-500/10 text-orange-400' : 'bg-blue-500/10 text-blue-400'}`}>
                  {t.priority}
                </span>
                <span className="text-sm text-slate-300">{t.description}</span>
                {t.completed ? <CheckCircle className="w-4 h-4 text-green-400 ml-auto" /> : <XCircle className="w-4 h-4 text-slate-600 ml-auto" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {lifecycle?.lifecycle_events?.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h4 className="text-white font-medium mb-3">Lifecycle Events</h4>
          <div className="space-y-2">
            {lifecycle.lifecycle_events.map((e, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <span className="text-slate-400">{e.from_phase}</span>
                <span className="text-slate-600">→</span>
                <span className="text-white">{e.to_phase}</span>
                <span className="text-slate-500 text-xs ml-auto">{e.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-slate-800/20 rounded-xl p-4 border border-slate-700/30">
        <h4 className="text-slate-400 text-sm mb-2">Agent Lifecycle Governance</h4>
        <p className="text-slate-500 text-xs">
          Formal procedures for offboarding agents: revoking credentials, draining tasks,
          archiving telemetry, wiping memory stores, and generating decommission certificates.
          Prevents toxic knowledge evaporation and orphaned memory caches.
        </p>
      </div>
    </div>
  );
}

function SBOMTab() {
  const [agentId, setAgentId] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState('');

  const scanSBOM = async () => {
    if (!agentId) return;
    setLoading('scan');
    try {
      const data = await api.scanAgentSBOM(agentId);
      setScanResult(data);
      toast.success(`Scan complete — ${data.risk_level} risk`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No SBOM found');
    }
    setLoading('');
  };

  const riskColor = (level) => {
    if (level === 'critical') return 'text-red-400 bg-red-500/10 border-red-500/20';
    if (level === 'high') return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
    if (level === 'medium') return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
    return 'text-green-400 bg-green-500/10 border-green-500/20';
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">Supply Chain Risk Scanner</h3>
        <div className="flex gap-3">
          <Input placeholder="Agent ID" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <Button onClick={scanSBOM} disabled={loading === 'scan'} className="bg-purple-600 hover:bg-purple-700 text-white">
            {loading === 'scan' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
            Scan SBOM
          </Button>
        </div>
      </div>

      {scanResult && (
        <div className="space-y-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm border ${riskColor(scanResult.risk_level)}`}>
                {scanResult.risk_level} risk — Score: {scanResult.risk_score}/100
              </span>
              <span className="text-slate-500 text-sm">{scanResult.total_deps_scanned} dependencies scanned</span>
            </div>
            {scanResult.findings?.length > 0 && (
              <div className="space-y-2">
                {scanResult.findings.map((f, i) => (
                  <div key={i} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className={`w-4 h-4 ${f.severity === 'critical' ? 'text-red-400' : f.severity === 'high' ? 'text-orange-400' : 'text-yellow-400'}`} />
                      <span className="text-white text-sm">{f.type.replace(/_/g, ' ')}</span>
                      {f.dep_name && <span className="text-slate-500 text-xs font-mono">{f.dep_name}</span>}
                    </div>
                    <p className="text-xs text-slate-400">{f.detail}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-slate-800/20 rounded-xl p-4 border border-slate-700/30">
        <h4 className="text-slate-400 text-sm mb-2">Agent Software Bill of Materials</h4>
        <p className="text-slate-500 text-xs">
          Tracks every tool, library, API dependency, and model used by each agent.
          Detects supply-chain risks: known-vulnerable packages, typosquat candidates,
          suspicious sources, and unverified/unsigned dependencies.
        </p>
      </div>
    </div>
  );
}

export default function GovernancePage() {
  return (
    <div className="min-h-screen bg-slate-900">
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white text-lg">AgentCheck</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/agents" className="text-slate-400 hover:text-white transition-colors text-sm">Agent Control Plane</Link>
            <Link to="/agents/detection" className="text-slate-400 hover:text-white transition-colors text-sm">Detection</Link>
            <span className="text-white font-medium text-sm">Governance</span>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <ShieldCheck className="w-6 h-6 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Agent Governance</h1>
          </div>
          <p className="text-slate-400 text-sm">
            Structural safety controls that address the five critical enterprise hygiene gaps:
            privilege escalation, cross-agent trust exploitation, compute cost proliferation,
            memory orphaning, and supply-chain attacks.
          </p>
        </div>

        <Tabs defaultValue="policy" className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700 mb-6">
            <TabsTrigger value="policy" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Lock className="w-4 h-4 mr-2" />Policies
            </TabsTrigger>
            <TabsTrigger value="trust" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Network className="w-4 h-4 mr-2" />Trust
            </TabsTrigger>
            <TabsTrigger value="circuit" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Zap className="w-4 h-4 mr-2" />Circuit Breakers
            </TabsTrigger>
            <TabsTrigger value="lifecycle" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Archive className="w-4 h-4 mr-2" />Lifecycle
            </TabsTrigger>
            <TabsTrigger value="sbom" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Package className="w-4 h-4 mr-2" />SBOM
            </TabsTrigger>
          </TabsList>

          <TabsContent value="policy"><PolicyTab /></TabsContent>
          <TabsContent value="trust"><TrustTab /></TabsContent>
          <TabsContent value="circuit"><CircuitBreakerTab /></TabsContent>
          <TabsContent value="lifecycle"><LifecycleTab /></TabsContent>
          <TabsContent value="sbom"><SBOMTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
